// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/command-registry — modify category descriptors
// ═══════════════════════════════════════════════════════════════════════════
//
// Category: "modify" — commands that operate on existing entities (MOVE,
// COPY, MIRROR, ROTATE, SCALE, OFFSET, STRETCH, TRIM, EXTEND, FILLET,
// CHAMFER, BREAK, JOIN, EXPLODE, LENGTHEN).
//
// Sources of truth used to author each descriptor (see plan):
//   • command type literals: packages/contracts/src/editor.ts:1039-1172
//   • aliases: apps/web/src/app/scene/draftingHelpers.ts:445-490
//     (normalizeDraftCommand switch). RU aliases that the switch does not
//     yet recognise are still authored here so the AI / help layer can
//     surface AutoCAD-CIS muscle-memory; the validator's alias-parity
//     check warns (does not error) when a registry alias is absent from
//     the switch — those are tracked as a follow-up to widen the switch.
//   • i18n labels: apps/web/src/app/i18n/messages.ts (command.*.label,
//     lines 1349-1420 RU / 3866-3933 EN).
//   • prompt keys: apps/web/src/app/i18n/messages.ts (prompt.*, lines
//     1352-1443 RU / 3869-3946 EN).
//   • draft step ids: apps/web/src/app/scene/draftingHelpers.ts:252-295
//   • initial step mapping: apps/web/src/app/scene/draftingHelpers.ts:560-574
//
// Every descriptor here has an implemented reducer in geometryReducer.ts,
// hence `status: "implemented"` across the board. COPY maps to two
// related literals — the `geometry/copy-entities` (delta-only) variant
// and the `geometry/copy` (basePoint+secondPoint) variant the draft
// state machine dispatches at commit so the exact pick pair is preserved
// for AI replay. EXPLODE likewise covers `geometry/explode-entities` and
// the specialised `geometry/explode-insert` for INSERT entities.
// ═══════════════════════════════════════════════════════════════════════════

import type { CommandDescriptor } from "../types.js";

const REDUCER_GEOMETRY = "apps/web/src/app/state/geometryReducer.ts" as const;

// ───────────────────────────── MOVE ───────────────────────────────────────
const MOVE: CommandDescriptor = {
  id: "MOVE",
  category: "modify",
  status: "implemented",
  aiCallable: true,
  aliases: { en: ["MOVE", "M"], ru: ["ПЕРЕНЕСТИ", "ПЕРЕН"] },
  commandTypeLiterals: ["geometry/move-entities"],
  draftCommandId: "MOVE",
  draftSteps: [
    {
      stepId: "move-select",
      promptKeyRu: "prompt.move.select",
      promptKeyEn: "prompt.move.select",
      expects: "selection",
    },
    {
      stepId: "move-base-point",
      promptKeyRu: "prompt.move.base",
      promptKeyEn: "prompt.move.base",
      expects: "point",
    },
    {
      stepId: "move-destination",
      promptKeyRu: "prompt.move.destination",
      promptKeyEn: "prompt.move.destination",
      expects: "point",
    },
  ],
  parameters: [
    {
      name: "entityIds",
      type: "entityIdList",
      required: true,
      description: {
        ru: "Список идентификаторов перемещаемых сущностей (минимум 1).",
        en: "List of entity ids to move (minimum 1).",
      },
    },
    {
      name: "delta",
      type: "point2d",
      required: true,
      description: {
        ru: "Вектор смещения (x, y), равный (точка назначения − базовая точка).",
        en: "Displacement vector (x, y) equal to (destination − base point).",
      },
    },
  ],
  help: {
    ru: {
      short: "Перемещает выбранные объекты на заданный вектор смещения.",
      long: "Команда ПЕРЕНЕСТИ сдвигает набор объектов на вектор, заданный двумя точками: базовой и точкой назначения. Итоговое смещение равно (точка назначения − базовая точка) и применяется ко всем объектам набора одновременно. Исходные объекты не копируются — их положение изменяется на месте. Если нужно сохранить оригиналы, используйте КОПИРОВАТЬ.",
      examples: ["ПЕРЕНЕСТИ (выбор) 0,0 100,50", "M (выбор) @100,0"],
      seeAlso: ["COPY", "STRETCH"],
    },
    en: {
      short: "Moves selected objects by a displacement vector.",
      long: "The MOVE command translates a set of objects by a vector given as two points: base point and destination. The displacement equals (destination − base point) and is applied to every object in the selection simultaneously. Source objects are not duplicated — they are translated in place. Use COPY if you need to keep the originals.",
      examples: ["MOVE (selection) 0,0 100,50", "M (selection) @100,0"],
      seeAlso: ["COPY", "STRETCH"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.move.label",
    labelKeyEn: "command.move.label",
    ribbonTab: "modify",
    ribbonSection: "ribbon.section.modify",
    glyph: "M",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/move-entities", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── COPY ───────────────────────────────────────
const COPY: CommandDescriptor = {
  id: "COPY",
  category: "modify",
  status: "implemented",
  aiCallable: true,
  aliases: { en: ["COPY", "CO", "CP"], ru: ["КОПИРОВАТЬ", "КОПИР"] },
  commandTypeLiterals: ["geometry/copy-entities", "geometry/copy"],
  draftCommandId: "COPY",
  draftSteps: [
    {
      stepId: "copy-select",
      promptKeyRu: "prompt.copy.select",
      promptKeyEn: "prompt.copy.select",
      expects: "selection",
    },
    {
      stepId: "copy-base-point",
      promptKeyRu: "prompt.copy.base_point",
      promptKeyEn: "prompt.copy.base_point",
      expects: "point",
    },
    {
      stepId: "copy-destination",
      promptKeyRu: "prompt.copy.second_or_exit",
      promptKeyEn: "prompt.copy.second_or_exit",
      expects: "point",
    },
  ],
  parameters: [
    {
      name: "entityIds",
      type: "entityIdList",
      required: true,
      description: {
        ru: "Список идентификаторов копируемых сущностей (минимум 1).",
        en: "List of entity ids to copy (minimum 1).",
      },
    },
    {
      name: "delta",
      type: "point2d",
      required: true,
      description: {
        ru: "Вектор смещения копии (для варианта geometry/copy-entities).",
        en: "Displacement vector for the copy (geometry/copy-entities variant).",
      },
    },
    {
      name: "basePoint",
      type: "point3d",
      required: false,
      description: {
        ru: "Базовая точка для варианта geometry/copy (используется в CLI / AI replay).",
        en: "Base point for the geometry/copy variant (used by CLI / AI replay).",
      },
    },
    {
      name: "secondPoint",
      type: "point3d",
      required: false,
      description: {
        ru: "Точка назначения второй копии для варианта geometry/copy.",
        en: "Second / destination point for the geometry/copy variant.",
      },
    },
  ],
  help: {
    ru: {
      short: "Создаёт копии выбранных объектов со смещением.",
      long: "Команда КОПИРОВАТЬ создаёт новые экземпляры выбранных объектов, смещённые на вектор (точка назначения − базовая точка). По умолчанию AutoCAD позволяет выполнять несколько последовательных копий в одной сессии — после указания первой точки назначения запрашивается следующая, и так до нажатия Enter / Esc. Исходные объекты остаются на месте.",
      examples: [
        "КОПИРОВАТЬ (выбор) 0,0 100,50",
        "CO (выбор) 0,0 @50,0 @100,0",
      ],
      seeAlso: ["MOVE", "ARRAYRECT"],
    },
    en: {
      short: "Creates offset copies of selected objects.",
      long: "The COPY command creates new instances of the selected objects displaced by (destination − base point). By default AutoCAD lets you drop several copies in one session — after the first destination it re-prompts for another, repeating until Enter / Escape. Source objects remain in place.",
      examples: [
        "COPY (selection) 0,0 100,50",
        "CO (selection) 0,0 @50,0 @100,0",
      ],
      seeAlso: ["MOVE", "ARRAYRECT"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.copy.label",
    labelKeyEn: "command.copy.label",
    ribbonTab: "modify",
    ribbonSection: "ribbon.section.modify",
    glyph: "CO",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/copy-entities", file: REDUCER_GEOMETRY },
    { commandTypeLiteral: "geometry/copy", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── MIRROR ─────────────────────────────────────
const MIRROR: CommandDescriptor = {
  id: "MIRROR",
  category: "modify",
  status: "implemented",
  aiCallable: true,
  aliases: { en: ["MIRROR", "MI"], ru: ["ЗЕРКАЛО", "ОТРАЗИТЬ"] },
  commandTypeLiterals: ["geometry/mirror-entities"],
  draftCommandId: "MIRROR",
  draftSteps: [
    {
      stepId: "mirror-select",
      promptKeyRu: "prompt.mirror.select",
      promptKeyEn: "prompt.mirror.select",
      expects: "selection",
    },
    {
      stepId: "mirror-first-point",
      promptKeyRu: "prompt.mirror.first",
      promptKeyEn: "prompt.mirror.first",
      expects: "point",
    },
    {
      stepId: "mirror-second-point",
      promptKeyRu: "prompt.mirror.second",
      promptKeyEn: "prompt.mirror.second",
      expects: "point",
    },
  ],
  parameters: [
    {
      name: "entityIds",
      type: "entityIdList",
      required: true,
      description: {
        ru: "Список идентификаторов зеркалируемых сущностей (минимум 1).",
        en: "List of entity ids to mirror (minimum 1).",
      },
    },
    {
      name: "mirrorLine1",
      type: "point2d",
      required: true,
      description: {
        ru: "Первая точка оси симметрии.",
        en: "First point of the mirror line.",
      },
    },
    {
      name: "mirrorLine2",
      type: "point2d",
      required: true,
      description: {
        ru: "Вторая точка оси симметрии.",
        en: "Second point of the mirror line.",
      },
    },
    {
      name: "deleteSource",
      type: "boolean",
      required: false,
      defaultValue: "false",
      description: {
        ru: "Удалять ли исходные объекты. По умолчанию false (копия зеркала рядом с оригиналом).",
        en: "Whether to delete the source objects. Defaults to false (keep the original alongside the mirrored copy).",
      },
    },
  ],
  help: {
    ru: {
      short: "Создаёт зеркальную копию объектов относительно указанной оси.",
      long: "Команда ЗЕРКАЛО создаёт зеркально отражённые копии выбранных объектов относительно прямой, заданной двумя точками. По умолчанию исходные объекты сохраняются (deleteSource = false); если включить опцию deleteSource, исходники будут удалены, оставив только отражённые версии. Для текстовых объектов (TEXT / MTEXT) поведение контролируется системной переменной MIRRTEXT.",
      examples: ["ЗЕРКАЛО (выбор) 0,0 0,100", "MI (выбор) 50,0 50,100"],
      seeAlso: ["ROTATE", "COPY"],
    },
    en: {
      short: "Creates a mirrored copy of objects about a specified axis.",
      long: "The MIRROR command creates mirror-imaged copies of selected objects across a line defined by two points. By default the source objects are retained (deleteSource = false); enabling deleteSource removes the sources and keeps only the mirrored versions. For text (TEXT / MTEXT) the behavior is gated by the MIRRTEXT system variable.",
      examples: ["MIRROR (selection) 0,0 0,100", "MI (selection) 50,0 50,100"],
      seeAlso: ["ROTATE", "COPY"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.mirror.label",
    labelKeyEn: "command.mirror.label",
    ribbonTab: "modify",
    ribbonSection: "ribbon.section.modify",
    glyph: "MI",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/mirror-entities", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── ROTATE ─────────────────────────────────────
const ROTATE: CommandDescriptor = {
  id: "ROTATE",
  category: "modify",
  status: "implemented",
  aiCallable: true,
  aliases: { en: ["ROTATE", "RO"], ru: ["ПОВЕРНУТЬ", "ПОВОРОТ"] },
  commandTypeLiterals: ["geometry/rotate-entities"],
  draftCommandId: "ROTATE",
  draftSteps: [
    {
      stepId: "rotate-select",
      promptKeyRu: "prompt.rotate.select",
      promptKeyEn: "prompt.rotate.select",
      expects: "selection",
    },
    {
      stepId: "rotate-base-point",
      promptKeyRu: "prompt.rotate.base",
      promptKeyEn: "prompt.rotate.base",
      expects: "point",
    },
    {
      stepId: "rotate-angle",
      promptKeyRu: "prompt.rotate.angle",
      promptKeyEn: "prompt.rotate.angle",
      expects: "angle",
    },
  ],
  parameters: [
    {
      name: "entityIds",
      type: "entityIdList",
      required: true,
      description: {
        ru: "Список идентификаторов поворачиваемых сущностей (минимум 1).",
        en: "List of entity ids to rotate (minimum 1).",
      },
    },
    {
      name: "basePoint",
      type: "point2d",
      required: true,
      description: {
        ru: "Точка вокруг которой выполняется поворот.",
        en: "Base point around which the rotation is applied.",
      },
    },
    {
      name: "angleDeg",
      type: "number",
      required: true,
      description: {
        ru: "Угол поворота в градусах, положительное значение — против часовой стрелки.",
        en: "Rotation angle in degrees; positive values rotate counter-clockwise.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Поворачивает выбранные объекты вокруг базовой точки на заданный угол.",
      long: "Команда ПОВЕРНУТЬ вращает выбранные объекты вокруг базовой точки на указанный угол в градусах. Положительный угол — поворот против часовой стрелки (в соответствии с системной переменной ANGDIR = 0 по умолчанию). Угол можно ввести численно или указать двумя точками (базовая + референсная) — в последнем случае угол определяется вектором от базовой точки до второй точки.",
      examples: ["ПОВЕРНУТЬ (выбор) 50,50 45", "RO (выбор) 0,0 90"],
      seeAlso: ["MIRROR", "SCALE"],
    },
    en: {
      short: "Rotates selected objects around a base point by a given angle.",
      long: "The ROTATE command rotates the selected objects around a base point by the specified angle in degrees. A positive angle rotates counter-clockwise (per the default ANGDIR = 0 system variable). The angle may be typed as a number or implied by a second reference point — in the latter case the angle is the vector from base to the second point.",
      examples: ["ROTATE (selection) 50,50 45", "RO (selection) 0,0 90"],
      seeAlso: ["MIRROR", "SCALE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.rotate.label",
    labelKeyEn: "command.rotate.label",
    ribbonTab: "modify",
    ribbonSection: "ribbon.section.modify",
    glyph: "RO",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/rotate-entities", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── SCALE ──────────────────────────────────────
const SCALE: CommandDescriptor = {
  id: "SCALE",
  category: "modify",
  status: "implemented",
  aiCallable: true,
  aliases: { en: ["SCALE", "SC"], ru: ["МАСШТАБ", "МАСШТАБИРОВАТЬ"] },
  commandTypeLiterals: ["geometry/scale-entities"],
  draftCommandId: "SCALE",
  draftSteps: [
    {
      stepId: "scale-select",
      promptKeyRu: "prompt.scale.select",
      promptKeyEn: "prompt.scale.select",
      expects: "selection",
    },
    {
      stepId: "scale-base-point",
      promptKeyRu: "prompt.scale.base",
      promptKeyEn: "prompt.scale.base",
      expects: "point",
    },
    {
      stepId: "scale-factor",
      promptKeyRu: "prompt.scale.factor",
      promptKeyEn: "prompt.scale.factor",
      expects: "distance",
    },
  ],
  parameters: [
    {
      name: "entityIds",
      type: "entityIdList",
      required: true,
      description: {
        ru: "Список идентификаторов масштабируемых сущностей (минимум 1).",
        en: "List of entity ids to scale (minimum 1).",
      },
    },
    {
      name: "basePoint",
      type: "point2d",
      required: true,
      description: {
        ru: "Неподвижная точка масштабирования.",
        en: "Fixed base point for the scaling operation.",
      },
    },
    {
      name: "factor",
      type: "number",
      required: true,
      description: {
        ru: "Масштабный коэффициент (>1 — увеличение, 0…1 — уменьшение). Отрицательные значения не допускаются.",
        en: "Scale factor (>1 enlarges, 0..1 shrinks). Negative values are not allowed.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Масштабирует объекты относительно базовой точки с указанным коэффициентом.",
      long: "Команда МАСШТАБ увеличивает или уменьшает выбранные объекты на заданный коэффициент относительно базовой точки. Коэффициент применяется равномерно по X и Y (AutoCAD не поддерживает неравномерное масштабирование напрямую). Базовая точка остаётся неподвижной; все остальные точки объектов отодвигаются/придвигаются к ней пропорционально коэффициенту. Значение 2 удваивает размер, 0.5 — уменьшает вдвое.",
      examples: ["МАСШТАБ (выбор) 0,0 2", "SC (выбор) 50,50 0.5"],
      seeAlso: ["STRETCH", "ROTATE"],
    },
    en: {
      short: "Scales objects relative to a base point by a given factor.",
      long: "The SCALE command enlarges or shrinks the selected objects by a given factor about a base point. The factor is applied uniformly along X and Y (AutoCAD does not support non-uniform scaling via this command). The base point stays fixed; every other point is pushed/pulled relative to it in proportion to the factor. A value of 2 doubles the size; 0.5 halves it.",
      examples: ["SCALE (selection) 0,0 2", "SC (selection) 50,50 0.5"],
      seeAlso: ["STRETCH", "ROTATE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.scale.label",
    labelKeyEn: "command.scale.label",
    ribbonTab: "modify",
    ribbonSection: "ribbon.section.modify",
    glyph: "SC",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/scale-entities", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── OFFSET ─────────────────────────────────────
const OFFSET: CommandDescriptor = {
  id: "OFFSET",
  category: "modify",
  status: "implemented",
  aiCallable: true,
  aliases: { en: ["OFFSET", "O"], ru: ["ПОДОБИЕ", "СМЕЩЕНИЕ"] },
  commandTypeLiterals: ["geometry/offset-entity"],
  draftCommandId: "OFFSET",
  draftSteps: [
    {
      stepId: "offset-distance",
      promptKeyRu: "prompt.offset.distance",
      promptKeyEn: "prompt.offset.distance",
      expects: "distance",
    },
    {
      stepId: "offset-select-entity",
      promptKeyRu: "prompt.offset.select",
      promptKeyEn: "prompt.offset.select",
      expects: "selection",
    },
    {
      stepId: "offset-side",
      promptKeyRu: "prompt.offset.side",
      promptKeyEn: "prompt.offset.side",
      expects: "point",
    },
  ],
  parameters: [
    {
      name: "entityId",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор исходной сущности, от которой строится подобие.",
        en: "Id of the source entity from which the parallel copy is built.",
      },
    },
    {
      name: "distance",
      type: "number",
      required: true,
      description: {
        ru: "Расстояние смещения (положительное число).",
        en: "Offset distance (positive number).",
      },
    },
    {
      name: "sidePoint",
      type: "point2d",
      required: true,
      description: {
        ru: "Точка, определяющая сторону, куда строится подобие.",
        en: "Point indicating the side on which the parallel copy is placed.",
      },
    },
  ],
  help: {
    ru: {
      short: "Создаёт параллельную копию объекта на заданном расстоянии.",
      long: "Команда ПОДОБИЕ (OFFSET) создаёт параллельную копию исходного объекта на заданном расстоянии. Направление смещения задаётся указанием точки на нужной стороне от исходного объекта. Для отрезков результат — параллельная линия той же длины; для полилиний — параллельная полилиния с корректным смещением дуговых сегментов; для окружностей и дуг — концентрическая окружность/дуга большего или меньшего радиуса.",
      examples: ["ПОДОБИЕ 10 (выбор) 50,50", "O 25 (выбор) 100,0"],
      seeAlso: ["COPY", "FILLET"],
    },
    en: {
      short: "Creates a parallel copy of an object at a specified distance.",
      long: "The OFFSET command creates a parallel copy of a source object at a given distance. The side of the offset is set by picking a point on the desired side of the source. For lines the result is a parallel line of the same length; for polylines, a parallel polyline with correctly-offset arc segments; for circles and arcs, a concentric circle/arc with a larger or smaller radius.",
      examples: ["OFFSET 10 (selection) 50,50", "O 25 (selection) 100,0"],
      seeAlso: ["COPY", "FILLET"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.offset.label",
    labelKeyEn: "command.offset.label",
    ribbonTab: "modify",
    ribbonSection: "ribbon.section.modify",
    glyph: "O",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/offset-entity", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── STRETCH ────────────────────────────────────
const STRETCH: CommandDescriptor = {
  id: "STRETCH",
  category: "modify",
  status: "implemented",
  aiCallable: true,
  aliases: { en: ["STRETCH", "S"], ru: ["РАСТЯНУТЬ", "РАСТЯЖ"] },
  commandTypeLiterals: ["geometry/stretch-entities"],
  draftCommandId: "STRETCH",
  draftSteps: [
    {
      stepId: "stretch-select-first",
      promptKeyRu: "prompt.stretch.first",
      promptKeyEn: "prompt.stretch.first",
      expects: "point",
    },
    {
      stepId: "stretch-select-second",
      promptKeyRu: "prompt.stretch.second",
      promptKeyEn: "prompt.stretch.second",
      expects: "point",
    },
    {
      stepId: "stretch-base",
      promptKeyRu: "prompt.stretch.base",
      promptKeyEn: "prompt.stretch.base",
      expects: "point",
    },
    {
      stepId: "stretch-destination",
      promptKeyRu: "prompt.stretch.destination",
      promptKeyEn: "prompt.stretch.destination",
      expects: "point",
    },
  ],
  parameters: [
    {
      name: "entityIds",
      type: "entityIdList",
      required: true,
      description: {
        ru: "Список идентификаторов сущностей, частично попавших в рамку выбора.",
        en: "List of entity ids partially captured by the crossing window.",
      },
    },
    {
      name: "stretchWindow",
      type: "object",
      required: true,
      description: {
        ru: "Прямоугольник выбора (x1, y1, x2, y2). Вершины внутри окна смещаются, вне — остаются на месте.",
        en: "Crossing-window rectangle (x1, y1, x2, y2). Vertices inside the window move; vertices outside stay fixed.",
      },
    },
    {
      name: "delta",
      type: "point2d",
      required: true,
      description: {
        ru: "Вектор смещения (x, y) для вершин внутри окна выбора.",
        en: "Displacement vector (x, y) applied to the vertices inside the window.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Растягивает или смещает части объектов, захваченных секущей рамкой.",
      long: "Команда РАСТЯНУТЬ изменяет форму объектов, частично попавших в секущую рамку выбора. Вершины, лежащие ВНУТРИ рамки, смещаются на заданный вектор; вершины СНАРУЖИ остаются неподвижными. Если объект целиком внутри рамки — команда работает как ПЕРЕНЕСТИ. Если целиком снаружи — объект не изменяется. Для окружностей и блоков растяжения не происходит: они перемещаются как единое целое, когда центр/точка вставки внутри рамки.",
      examples: [
        "РАСТЯНУТЬ 0,0 100,100 50,50 80,80",
        "S 10,10 90,90 0,0 @20,0",
      ],
      seeAlso: ["MOVE", "SCALE"],
    },
    en: {
      short:
        "Stretches or moves parts of objects captured by a crossing selection window.",
      long: "The STRETCH command reshapes objects partially captured by a crossing selection window. Vertices INSIDE the window are translated by the displacement vector; vertices OUTSIDE stay fixed. If an object is wholly inside the window, STRETCH behaves like MOVE. If wholly outside, the object is unaffected. Circles and block inserts don't stretch: they move as a unit when their center / insertion point is inside the window.",
      examples: ["STRETCH 0,0 100,100 50,50 80,80", "S 10,10 90,90 0,0 @20,0"],
      seeAlso: ["MOVE", "SCALE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.stretch.label",
    labelKeyEn: "command.stretch.label",
    ribbonTab: "modify",
    ribbonSection: "ribbon.section.modify",
    glyph: "S",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/stretch-entities", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── TRIM ───────────────────────────────────────
const TRIM: CommandDescriptor = {
  id: "TRIM",
  category: "modify",
  status: "implemented",
  aiCallable: true,
  aliases: { en: ["TRIM", "TR"], ru: ["ОБРЕЗАТЬ", "ОБРЕЗ"] },
  commandTypeLiterals: ["geometry/trim-entity"],
  draftCommandId: "TRIM",
  draftSteps: [
    {
      stepId: "trim-select-edges",
      promptKeyRu: "prompt.trim.select_edges",
      promptKeyEn: "prompt.trim.select_edges",
      expects: "selection",
    },
    {
      stepId: "trim-pick-entity",
      promptKeyRu: "prompt.trim.pick_entity",
      promptKeyEn: "prompt.trim.pick_entity",
      expects: "point",
    },
  ],
  parameters: [
    {
      name: "entityId",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор обрезаемой сущности.",
        en: "Id of the entity to trim.",
      },
    },
    {
      name: "cuttingEdgeIds",
      type: "entityIdList",
      required: true,
      description: {
        ru: "Идентификаторы режущих кромок (минимум 1).",
        en: "Ids of the cutting-edge entities (minimum 1).",
      },
    },
    {
      name: "pickPoint",
      type: "point2d",
      required: true,
      description: {
        ru: "Точка на обрезаемом объекте, указывающая удаляемую часть.",
        en: "Point on the target entity that identifies the portion to remove.",
      },
    },
  ],
  help: {
    ru: {
      short: "Обрезает часть объекта по указанным режущим кромкам.",
      long: "Команда ОБРЕЗАТЬ удаляет участок выбранного объекта, заключённый между точкой клика и ближайшими пересечениями с режущими кромками. Сначала выбираются режущие кромки (любые линейные/кривые объекты, пересекающиеся с целевыми), затем последовательно кликается удаляемая часть. В AutoCAD 2021+ режим Quick Trim позволяет опустить явный выбор кромок — все видимые объекты рассматриваются как потенциальные кромки.",
      examples: [
        "ОБРЕЗАТЬ (кромки) Enter (клик на удаляемой части)",
        "TR (кромки) (клик)",
      ],
      seeAlso: ["EXTEND", "BREAK"],
    },
    en: {
      short: "Trims part of an object against specified cutting edges.",
      long: "The TRIM command removes a portion of the selected entity bounded by the pick point and the nearest intersections with the cutting edges. First pick the cutting edges (any linear / curve entities that cross the targets), then click the portion to remove. AutoCAD 2021+ offers a Quick Trim mode where the edge selection step is skipped — every visible object is considered a potential edge.",
      examples: [
        "TRIM (edges) Enter (click on discarded part)",
        "TR (edges) (click)",
      ],
      seeAlso: ["EXTEND", "BREAK"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.trim.label",
    labelKeyEn: "command.trim.label",
    ribbonTab: "modify",
    ribbonSection: "ribbon.section.modify",
    glyph: "TR",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/trim-entity", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── EXTEND ─────────────────────────────────────
const EXTEND: CommandDescriptor = {
  id: "EXTEND",
  category: "modify",
  status: "implemented",
  aiCallable: true,
  aliases: { en: ["EXTEND", "EX"], ru: ["УДЛИНИТЬ", "УДЛ"] },
  commandTypeLiterals: ["geometry/extend-entity"],
  draftCommandId: "EXTEND",
  draftSteps: [
    {
      stepId: "extend-select-edges",
      promptKeyRu: "prompt.extend.select_edges",
      promptKeyEn: "prompt.extend.select_edges",
      expects: "selection",
    },
    {
      stepId: "extend-pick-entity",
      promptKeyRu: "prompt.extend.pick_entity",
      promptKeyEn: "prompt.extend.pick_entity",
      expects: "point",
    },
  ],
  parameters: [
    {
      name: "entityId",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор удлиняемой сущности.",
        en: "Id of the entity to extend.",
      },
    },
    {
      name: "boundaryEdgeIds",
      type: "entityIdList",
      required: true,
      description: {
        ru: "Идентификаторы граничных кромок (минимум 1).",
        en: "Ids of the boundary-edge entities (minimum 1).",
      },
    },
    {
      name: "pickPoint",
      type: "point2d",
      required: true,
      description: {
        ru: "Точка на удлиняемом объекте ближе к тому концу, который требуется удлинить.",
        en: "Point on the target entity closer to the end that should be extended.",
      },
    },
  ],
  help: {
    ru: {
      short: "Удлиняет объект до указанных граничных кромок.",
      long: "Команда УДЛИНИТЬ продлевает выбранный объект до пересечения с граничной кромкой. Сначала выбираются граничные кромки (любые линейные/кривые объекты), затем кликается конец объекта, который нужно удлинить. Если объект не пересекает выбранные границы при естественном продолжении — AutoCAD выдаёт предупреждение без изменений. УДЛИНИТЬ работает парой с ОБРЕЗАТЬ и часто используется одновременно (Shift+клик переключает режимы).",
      examples: [
        "УДЛИНИТЬ (границы) Enter (клик на удлиняемом конце)",
        "EX (границы) (клик)",
      ],
      seeAlso: ["TRIM", "LENGTHEN"],
    },
    en: {
      short: "Extends an object to meet specified boundary edges.",
      long: "The EXTEND command lengthens the selected entity until it meets a boundary edge. First pick the boundary edges (any linear / curve entities), then click the end of the entity that should be extended. If the entity would not hit the selected boundaries even when extrapolated, AutoCAD warns and leaves it unchanged. EXTEND pairs with TRIM and they're often used interchangeably (Shift+click toggles modes).",
      examples: [
        "EXTEND (boundaries) Enter (click on end to extend)",
        "EX (boundaries) (click)",
      ],
      seeAlso: ["TRIM", "LENGTHEN"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.extend.label",
    labelKeyEn: "command.extend.label",
    ribbonTab: "modify",
    ribbonSection: "ribbon.section.modify",
    glyph: "EX",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/extend-entity", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── FILLET ─────────────────────────────────────
// NOTE: "ФАСКА" is the canonical AutoCAD-CIS alias for CHAMFER, NOT
// FILLET. The prompt table mentioned both — we list only "СОПРЯЖЕНИЕ"
// here to avoid an alias collision with CHAMFER. "КРУГЛ" (short for
// "круглое сопряжение") is an AutoCAD-CIS helper alias some users type.
const FILLET: CommandDescriptor = {
  id: "FILLET",
  category: "modify",
  status: "implemented",
  // aiCallable:false — not in ТЗ §10.1; AI surface deferred to CC-2c + explicit ТЗ §17 revision
  aiCallable: false,
  aliases: { en: ["FILLET", "F"], ru: ["СОПРЯЖЕНИЕ", "КРУГЛ"] },
  commandTypeLiterals: ["geometry/fillet-entities"],
  draftCommandId: "FILLET",
  draftSteps: [
    {
      stepId: "fillet-radius",
      promptKeyRu: "prompt.fillet.radius",
      promptKeyEn: "prompt.fillet.radius",
      expects: "distance",
    },
    {
      stepId: "fillet-first-entity",
      promptKeyRu: "prompt.fillet.first",
      promptKeyEn: "prompt.fillet.first",
      expects: "selection",
    },
    {
      stepId: "fillet-second-entity",
      promptKeyRu: "prompt.fillet.second",
      promptKeyEn: "prompt.fillet.second",
      expects: "selection",
    },
  ],
  parameters: [
    {
      name: "entityId1",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор первого объекта для сопряжения.",
        en: "Id of the first entity to fillet.",
      },
    },
    {
      name: "entityId2",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор второго объекта для сопряжения.",
        en: "Id of the second entity to fillet.",
      },
    },
    {
      name: "radius",
      type: "number",
      required: true,
      description: {
        ru: "Радиус сопрягающей дуги (неотрицательное число). 0 — острый угол без дуги.",
        en: "Radius of the fillet arc (non-negative). 0 produces a sharp corner without an arc.",
      },
    },
    {
      name: "pickPoint1",
      type: "point2d",
      required: true,
      description: {
        ru: "Точка на первом объекте ближе к сохраняемому концу.",
        en: "Point on the first entity closer to the end that should be retained.",
      },
    },
    {
      name: "pickPoint2",
      type: "point2d",
      required: true,
      description: {
        ru: "Точка на втором объекте ближе к сохраняемому концу.",
        en: "Point on the second entity closer to the end that should be retained.",
      },
    },
  ],
  help: {
    ru: {
      short: "Сопрягает два объекта дугой заданного радиуса.",
      long: "Команда СОПРЯЖЕНИЕ соединяет два объекта плавной дугой заданного радиуса и обрезает лишние части, чтобы получить аккуратный сглаженный угол. Сначала вводится радиус сопряжения, затем последовательно выбираются два объекта (обычно отрезки, но допустимы дуги, полилинии и окружности). Радиус 0 даёт острый угол — команда просто продлевает/обрезает объекты до точки пересечения без дуги.",
      examples: [
        "СОПРЯЖЕНИЕ 10 (объект 1) (объект 2)",
        "F 5 (линия 1) (линия 2)",
      ],
      seeAlso: ["CHAMFER", "TRIM"],
    },
    en: {
      short: "Fillets two entities with an arc of a given radius.",
      long: "The FILLET command connects two entities with a smooth arc of the specified radius and trims the leftover parts, producing a clean rounded corner. First type the fillet radius, then pick the two entities (usually lines, but arcs, polylines and circles are also accepted). Radius 0 produces a sharp corner — the command simply extends/trims the entities to their intersection point without an arc.",
      examples: ["FILLET 10 (entity 1) (entity 2)", "F 5 (line 1) (line 2)"],
      seeAlso: ["CHAMFER", "TRIM"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.fillet.label",
    labelKeyEn: "command.fillet.label",
    ribbonTab: "modify",
    ribbonSection: "ribbon.section.modify",
    glyph: "F",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/fillet-entities", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── CHAMFER ────────────────────────────────────
const CHAMFER: CommandDescriptor = {
  id: "CHAMFER",
  category: "modify",
  status: "implemented",
  // aiCallable:false — not in ТЗ §10.1; AI surface deferred to CC-2c + explicit ТЗ §17 revision
  aiCallable: false,
  aliases: { en: ["CHAMFER", "CHA"], ru: ["ФАСКА"] },
  commandTypeLiterals: ["geometry/chamfer-entities"],
  draftCommandId: "CHAMFER",
  draftSteps: [
    {
      stepId: "chamfer-distances",
      promptKeyRu: "prompt.chamfer.distances",
      promptKeyEn: "prompt.chamfer.distances",
      expects: "distance",
    },
    {
      stepId: "chamfer-first",
      promptKeyRu: "prompt.chamfer.first",
      promptKeyEn: "prompt.chamfer.first",
      expects: "selection",
    },
    {
      stepId: "chamfer-second",
      promptKeyRu: "prompt.chamfer.second",
      promptKeyEn: "prompt.chamfer.second",
      expects: "selection",
    },
  ],
  parameters: [
    {
      name: "entityId1",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор первого отрезка для снятия фаски.",
        en: "Id of the first entity to chamfer.",
      },
    },
    {
      name: "entityId2",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор второго отрезка для снятия фаски.",
        en: "Id of the second entity to chamfer.",
      },
    },
    {
      name: "dist1",
      type: "number",
      required: true,
      description: {
        ru: "Расстояние фаски вдоль первого отрезка (неотрицательное).",
        en: "Chamfer distance along the first entity (non-negative).",
      },
    },
    {
      name: "dist2",
      type: "number",
      required: true,
      description: {
        ru: "Расстояние фаски вдоль второго отрезка (неотрицательное).",
        en: "Chamfer distance along the second entity (non-negative).",
      },
    },
    {
      name: "pickPoint1",
      type: "point2d",
      required: true,
      description: {
        ru: "Точка на первом отрезке ближе к сохраняемому концу.",
        en: "Point on the first entity closer to the end that should be retained.",
      },
    },
    {
      name: "pickPoint2",
      type: "point2d",
      required: true,
      description: {
        ru: "Точка на втором отрезке ближе к сохраняемому концу.",
        en: "Point on the second entity closer to the end that should be retained.",
      },
    },
  ],
  help: {
    ru: {
      short: "Снимает фаску на сопряжении двух отрезков.",
      long: "Команда ФАСКА обрезает угол между двумя пересекающимися отрезками, заменяя его прямым сегментом. Вводятся два расстояния (по одному вдоль каждого отрезка), затем выбираются сами отрезки — команда убирает лишние части и вставляет прямую фаску. Если расстояния равны, получается симметричная фаска; разные значения дают асимметричный скос. Для плавных скруглений используйте СОПРЯЖЕНИЕ.",
      examples: [
        "ФАСКА 10 10 (отрезок 1) (отрезок 2)",
        "CHA 5 8 (линия 1) (линия 2)",
      ],
      seeAlso: ["FILLET", "TRIM"],
    },
    en: {
      short: "Chamfers the corner between two line segments.",
      long: "The CHAMFER command cuts the corner between two intersecting line segments and replaces it with a straight bevel. Enter two distances (one along each segment), then pick the segments — the command removes the leftover parts and inserts a straight chamfer. Equal distances produce a symmetric chamfer; unequal distances produce an asymmetric bevel. For smooth rounds use FILLET instead.",
      examples: [
        "CHAMFER 10 10 (segment 1) (segment 2)",
        "CHA 5 8 (line 1) (line 2)",
      ],
      seeAlso: ["FILLET", "TRIM"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.chamfer.label",
    labelKeyEn: "command.chamfer.label",
    ribbonTab: "modify",
    ribbonSection: "ribbon.section.modify",
    glyph: "CHA",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/chamfer-entities", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── BREAK ──────────────────────────────────────
const BREAK: CommandDescriptor = {
  id: "BREAK",
  category: "modify",
  status: "implemented",
  // aiCallable:false — not in ТЗ §10.1; AI surface deferred to CC-2c + explicit ТЗ §17 revision
  aiCallable: false,
  aliases: { en: ["BREAK", "BR"], ru: ["РАЗОРВАТЬ", "РАЗРЫВ"] },
  commandTypeLiterals: ["geometry/break-entity"],
  draftCommandId: "BREAK",
  draftSteps: [
    {
      stepId: "break-select",
      promptKeyRu: "prompt.break.select",
      promptKeyEn: "prompt.break.select",
      expects: "selection",
    },
    {
      stepId: "break-first-point",
      promptKeyRu: "prompt.break.first",
      promptKeyEn: "prompt.break.first",
      expects: "point",
    },
    {
      stepId: "break-second-point",
      promptKeyRu: "prompt.break.second",
      promptKeyEn: "prompt.break.second",
      expects: "point",
    },
  ],
  parameters: [
    {
      name: "entityId",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор разрываемой сущности.",
        en: "Id of the entity to break.",
      },
    },
    {
      name: "point1",
      type: "point2d",
      required: true,
      description: {
        ru: "Первая точка разрыва на объекте.",
        en: "First break point on the entity.",
      },
    },
    {
      name: "point2",
      type: "point2d",
      required: true,
      description: {
        ru: "Вторая точка разрыва. Если совпадает с первой — объект разделяется в одной точке без удаления участка.",
        en: "Second break point. If equal to the first, the entity is split at that point with no gap.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Разделяет объект на две части, удаляя участок между двумя точками.",
      long: "Команда РАЗОРВАТЬ (BREAK) разрывает выбранный объект в двух точках и удаляет участок между ними. Если обе точки совпадают (ввод @0,0 или клик в одну точку), объект просто разделяется без удаления материала — полезно для подготовки концов под последующую обработку. Команда работает с отрезками, полилиниями, окружностями, дугами, эллипсами и сплайнами.",
      examples: ["РАЗОРВАТЬ (объект) 20,0 80,0", "BR (объект) 50,50 @0,0"],
      seeAlso: ["TRIM", "JOIN"],
    },
    en: {
      short:
        "Breaks an object into two parts by removing the segment between two points.",
      long: "The BREAK command splits the selected entity at two points and removes the segment in between. If both points are equal (enter @0,0 or click the same spot twice), the entity is simply split with no material removed — handy for preparing endpoints for further processing. Works with lines, polylines, circles, arcs, ellipses and splines.",
      examples: ["BREAK (entity) 20,0 80,0", "BR (entity) 50,50 @0,0"],
      seeAlso: ["TRIM", "JOIN"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.break.label",
    labelKeyEn: "command.break.label",
    ribbonTab: "modify",
    ribbonSection: "ribbon.section.modify",
    glyph: "BR",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/break-entity", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── JOIN ───────────────────────────────────────
const JOIN: CommandDescriptor = {
  id: "JOIN",
  category: "modify",
  status: "implemented",
  // aiCallable:false — not in ТЗ §10.1; AI surface deferred to CC-2c + explicit ТЗ §17 revision
  aiCallable: false,
  aliases: { en: ["JOIN", "J"], ru: ["СОЕДИНИТЬ", "СОЕД"] },
  commandTypeLiterals: ["geometry/join-entities"],
  draftCommandId: "JOIN",
  draftSteps: [
    {
      stepId: "join-select",
      promptKeyRu: "prompt.join.select",
      promptKeyEn: "prompt.join.select",
      expects: "selection",
    },
  ],
  parameters: [
    {
      name: "entityIds",
      type: "entityIdList",
      required: true,
      description: {
        ru: "Список идентификаторов объединяемых сущностей (минимум 2).",
        en: "List of entity ids to join (minimum 2).",
      },
    },
  ],
  help: {
    ru: {
      short: "Объединяет несколько смежных объектов в одну сущность.",
      long: "Команда СОЕДИНИТЬ соединяет несколько коллинеарных или соприкасающихся отрезков, полилиний или дуг в одну сущность. Для отрезков требуется коллинеарность и совпадение концов; для полилиний — общий конечный узел; для дуг — общий центр и радиус. Результат зависит от типа входных объектов: несколько отрезков → одна LINE или LWPOLYLINE; дуги → одна ARC или CIRCLE при замыкании окружности.",
      examples: ["СОЕДИНИТЬ (объект 1) (объект 2) Enter", "J (отрезки) Enter"],
      seeAlso: ["BREAK", "PLINE"],
    },
    en: {
      short: "Joins multiple adjacent objects into a single entity.",
      long: "The JOIN command combines several collinear or touching lines, polylines or arcs into one entity. Lines must be collinear with matching endpoints; polylines must share an endpoint vertex; arcs must share center and radius. The output type depends on the inputs: several lines → one LINE or LWPOLYLINE; arcs → one ARC, or a CIRCLE when a full revolution is closed.",
      examples: ["JOIN (entity 1) (entity 2) Enter", "J (lines) Enter"],
      seeAlso: ["BREAK", "PLINE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.join.label",
    labelKeyEn: "command.join.label",
    ribbonTab: "modify",
    ribbonSection: "ribbon.section.modify",
    glyph: "J",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/join-entities", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── EXPLODE ────────────────────────────────────
const EXPLODE: CommandDescriptor = {
  id: "EXPLODE",
  category: "modify",
  status: "implemented",
  aiCallable: true,
  aliases: { en: ["EXPLODE", "X"], ru: ["РАСЧЛЕНИТЬ", "РАСЧ"] },
  commandTypeLiterals: ["geometry/explode-entities", "geometry/explode-insert"],
  draftCommandId: "EXPLODE",
  draftSteps: [
    {
      stepId: "explode-select",
      promptKeyRu: "prompt.explode.select",
      promptKeyEn: "prompt.explode.select",
      expects: "selection",
    },
  ],
  parameters: [
    {
      name: "entityIds",
      type: "entityIdList",
      required: true,
      description: {
        ru: "Идентификаторы сущностей для расчленения (минимум 1). Для варианта explode-entities.",
        en: "Ids of entities to explode (minimum 1). For the explode-entities variant.",
      },
    },
    {
      name: "insertId",
      type: "entityId",
      required: false,
      description: {
        ru: "Идентификатор INSERT для расчленения (вариант explode-insert).",
        en: "INSERT entity id to explode (explode-insert variant).",
      },
    },
    {
      name: "resolveNested",
      type: "enum",
      enumValues: ["none", "one-level"],
      required: false,
      defaultValue: "one-level",
      description: {
        ru: "Режим разрешения вложенных блоков: 'none' — не разворачивать вложенные INSERT'ы, 'one-level' — развернуть один уровень.",
        en: "Nested-block resolution mode: 'none' — keep nested INSERTs intact, 'one-level' — expand one level of nesting.",
      },
    },
  ],
  help: {
    ru: {
      short: "Разбивает сложный объект на составные примитивы.",
      long: "Команда РАСЧЛЕНИТЬ разбивает сложные сущности (BLOCK-вставки, полилинии, размеры, мультивыноски, штриховки, таблицы) на составляющие их примитивы. Для INSERT-блока создаются копии всех дочерних сущностей блока с применённой матрицей трансформации; атрибуты ATTRIB становятся TEXT-объектами. Для LWPOLYLINE — набор LINE/ARC сегментов. Некоторые свойства (BYBLOCK цвета, переменная ширина) могут быть потеряны. Используйте с осторожностью — операция обычно необратима кроме Undo.",
      examples: ["РАСЧЛЕНИТЬ (выбор) Enter", "X (блок) Enter"],
      seeAlso: ["JOIN", "BLOCK"],
    },
    en: {
      short: "Explodes a compound object into its constituent primitives.",
      long: "The EXPLODE command breaks compound entities (BLOCK inserts, polylines, dimensions, multileaders, hatches, tables) into the primitives they are built from. For an INSERT, copies of every child entity are emitted with the insert's transform applied; ATTRIB entities become TEXT objects. For an LWPOLYLINE the result is a set of LINE/ARC segments. Some properties (BYBLOCK colours, variable width) may be lost. Use with care — apart from Undo the operation is not reversible.",
      examples: ["EXPLODE (selection) Enter", "X (block) Enter"],
      seeAlso: ["JOIN", "BLOCK"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.explode.label",
    labelKeyEn: "command.explode.label",
    ribbonTab: "modify",
    ribbonSection: "ribbon.section.modify",
    glyph: "X",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/explode-entities", file: REDUCER_GEOMETRY },
    { commandTypeLiteral: "geometry/explode-insert", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── LENGTHEN ───────────────────────────────────
const LENGTHEN: CommandDescriptor = {
  id: "LENGTHEN",
  category: "modify",
  status: "implemented",
  // aiCallable:false — not in ТЗ §10.1; AI surface deferred to CC-2c + explicit ТЗ §17 revision
  aiCallable: false,
  aliases: { en: ["LENGTHEN", "LEN"], ru: ["УВЕЛИЧИТЬ", "УВЕЛ"] },
  commandTypeLiterals: ["geometry/lengthen-entity"],
  draftCommandId: "LENGTHEN",
  draftSteps: [
    {
      stepId: "lengthen-select",
      promptKeyRu: "prompt.lengthen.select",
      promptKeyEn: "prompt.lengthen.select",
      expects: "selection",
    },
    {
      stepId: "lengthen-mode",
      promptKeyRu: "prompt.lengthen.mode",
      promptKeyEn: "prompt.lengthen.mode",
      expects: "keyword",
      keywords: ["DE", "P", "T"],
    },
    {
      stepId: "lengthen-value",
      promptKeyRu: "prompt.lengthen.value",
      promptKeyEn: "prompt.lengthen.value",
      expects: "distance",
    },
    {
      stepId: "lengthen-pick-end",
      promptKeyRu: "prompt.lengthen.pick_end",
      promptKeyEn: "prompt.lengthen.pick_end",
      expects: "point",
    },
  ],
  parameters: [
    {
      name: "entityId",
      type: "entityId",
      required: true,
      description: {
        ru: "Идентификатор удлиняемой/укорачиваемой сущности.",
        en: "Id of the entity to lengthen / shorten.",
      },
    },
    {
      name: "mode",
      type: "enum",
      enumValues: ["delta", "percent", "total"],
      required: true,
      description: {
        ru: "Режим изменения длины: delta — на приращение, percent — в процентах от текущей длины, total — до заданной абсолютной длины.",
        en: "Length-change mode: delta — add the value, percent — treat value as a percentage of current length, total — set the absolute length.",
      },
    },
    {
      name: "value",
      type: "number",
      required: true,
      description: {
        ru: "Числовое значение для режима: для delta — приращение (может быть отрицательным), для percent — процент (100 = без изменений), для total — итоговая длина.",
        en: "Numeric value for the chosen mode: for delta — the increment (may be negative), for percent — the percentage (100 = no change), for total — the final length.",
      },
    },
    {
      name: "endPoint",
      type: "point2d",
      required: true,
      description: {
        ru: "Точка на объекте, указывающая, какой конец изменять.",
        en: "Point on the entity identifying which end to modify.",
      },
    },
  ],
  help: {
    ru: {
      short:
        "Изменяет длину отрезка или дуги на приращение, процент или до заданной длины.",
      long: "Команда УВЕЛИЧИТЬ (LENGTHEN) изменяет длину отрезков, дуг и открытых полилиний в одном из трёх режимов: ПРиращение (DE) — добавляет указанное число к текущей длине (отрицательное значение укорачивает); Процент (P) — задаёт новую длину как процент от текущей (100 = без изменений); Т — задаёт итоговую длину напрямую. После ввода режима и значения кликается конец объекта, который должен измениться. В отличие от УДЛИНИТЬ, здесь не нужны граничные кромки.",
      examples: [
        "УВЕЛИЧИТЬ (объект) DE 10 (конец)",
        "LEN (объект) P 150 (конец)",
      ],
      seeAlso: ["EXTEND", "TRIM"],
    },
    en: {
      short:
        "Changes the length of a line or arc by delta, percent, or to a total length.",
      long: "The LENGTHEN command changes the length of lines, arcs and open polylines using one of three modes: DElta (DE) — adds the given value to the current length (negative shortens); Percent (P) — sets the new length as a percentage of the current (100 = unchanged); Total (T) — sets the absolute final length. After mode and value you click the end of the entity that should change. Unlike EXTEND, no boundary edges are needed.",
      examples: ["LENGTHEN (entity) DE 10 (end)", "LEN (entity) P 150 (end)"],
      seeAlso: ["EXTEND", "TRIM"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.lengthen.label",
    labelKeyEn: "command.lengthen.label",
    ribbonTab: "modify",
    ribbonSection: "ribbon.section.modify",
    glyph: "LEN",
  },
  reducerLocations: [
    { commandTypeLiteral: "geometry/lengthen-entity", file: REDUCER_GEOMETRY },
  ],
};

// ───────────────────────────── ERASE ──────────────────────────────────────
// AutoCAD ERASE (alias E) — remove selected objects from the drawing. Owns the
// `shell/delete-entities` command literal: the SAME command the Delete key
// dispatches (useKeyboardShortcuts.ts) and the AI safe-contour routes
// DELETE_ENTITIES through. There is no new write path — this descriptor simply
// makes the existing, already-undoable delete an AI-callable + CLI-typed surface
// (the forward; the inverse `shell/restore-entities` makes it undoable). No
// draft state-machine flow: ERASE is a selection-then-act command (select
// objects, then Enter / Delete), so it carries no `draftCommandId` / draftSteps.
const ERASE: CommandDescriptor = {
  id: "ERASE",
  category: "modify",
  status: "implemented",
  aiCallable: true,
  aliases: { en: ["ERASE", "E"], ru: ["СТЕРЕТЬ", "УДАЛИТЬ"] },
  commandTypeLiterals: ["shell/delete-entities"],
  parameters: [
    {
      name: "entityIds",
      type: "entityIdList",
      required: true,
      description: {
        ru: "Список идентификаторов удаляемых сущностей (минимум 1).",
        en: "List of entity ids to erase (minimum 1).",
      },
    },
  ],
  help: {
    ru: {
      short: "Удаляет выбранные объекты из чертежа.",
      long: "Команда СТЕРЕТЬ (ERASE) удаляет выбранные объекты из чертежа. Сначала выбираются объекты, затем команда удаляет их все одновременно. Удаление обратимо: ОТМЕНИТЬ (UNDO) восстанавливает удалённые объекты с сохранением всех их свойств и идентификаторов. Несуществующие или уже удалённые идентификаторы игнорируются без ошибки. Это тот же механизм удаления, что и клавиша Delete.",
      examples: ["СТЕРЕТЬ (выбор) Enter", "E (выбор) Enter"],
      seeAlso: ["MOVE", "EXPLODE"],
    },
    en: {
      short: "Erases selected objects from the drawing.",
      long: "The ERASE command removes the selected objects from the drawing. First select the objects, then the command deletes them all at once. The deletion is reversible: UNDO restores the erased objects with every property and id preserved. Non-existent or already-deleted ids are ignored without error. This is the same delete mechanism as the Delete key.",
      examples: ["ERASE (selection) Enter", "E (selection) Enter"],
      seeAlso: ["MOVE", "EXPLODE"],
    },
  },
  uiMetadata: {
    labelKeyRu: "command.erase.label",
    labelKeyEn: "command.erase.label",
    ribbonTab: "modify",
    ribbonSection: "ribbon.section.modify",
    glyph: "E",
  },
  reducerLocations: [
    // The geometry-document arm (entity removal) lives in geometryReducer; the
    // shell-state arm (selection clear + audit) mirrors it in shellUiReducer.
    { commandTypeLiteral: "shell/delete-entities", file: REDUCER_GEOMETRY },
    {
      commandTypeLiteral: "shell/delete-entities",
      file: "apps/web/src/app/state/shellUiReducer.ts",
    },
  ],
};

// ───────────────────────────── EXPORT ─────────────────────────────────────
export const MODIFY_DESCRIPTORS: readonly CommandDescriptor[] = [
  BREAK,
  CHAMFER,
  COPY,
  ERASE,
  EXPLODE,
  EXTEND,
  FILLET,
  JOIN,
  LENGTHEN,
  MIRROR,
  MOVE,
  OFFSET,
  ROTATE,
  SCALE,
  STRETCH,
  TRIM,
];
